import { type FC } from "react";
import { useSnackbar } from "notistack";

import { Switch } from "@/shared/components/ui/switch";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUpdateNotifications } from "@/shared/hooks/useUpdateUser";
import { useLawyerSettings } from "@/shared/hooks/useLawyerSettings";
import { cn } from "@/shared/utils/core";

interface NotificationsSectionProps {
  onScrollToCellPhone?: () => void;
}

export const NotificationsSection: FC<NotificationsSectionProps> = ({ onScrollToCellPhone }) => {
  const { user, refetch } = useAppContext();
  const { lawyer } = useLawyerSettings();
  const { updateNotifications, isUpdating } = useUpdateNotifications();
  const { enqueueSnackbar } = useSnackbar();

  const contactByEmail = user?.contact_by_email ?? true;
  const contactByPhone = user?.contact_by_phone ?? false;
  const hasCellPhone = !!lawyer?.cell_phone;

  const handleToggle = (field: "contact_by_email" | "contact_by_phone", value: boolean) => {
    if (field === "contact_by_phone" && value && !hasCellPhone) {
      return;
    }

    updateNotifications(
      { [field]: value },
      {
        onSuccess: () => {
          enqueueSnackbar("Preferences updated.", { variant: "success" });
          refetch();
        },
        onError: () => {
          enqueueSnackbar("Could not update preferences. Please try again.", { variant: "error" });
        },
      },
    );
  };

  return (
    <div className="rounded-lg border border-primary bg-white overflow-hidden">
      <div className="p-6">
        <h4>Notification preferences</h4>
      </div>

      <div className="border-t border-primary" />

      <div className="p-6 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <p className="text-sm font-medium text-secondary">Email notifications</p>
            <p className="text-sm text-tertiary">Receive updates via email</p>
          </div>
          <Switch
            checked={contactByEmail}
            onCheckedChange={(checked) => handleToggle("contact_by_email", checked)}
            disabled={isUpdating}
            aria-label="Email notifications"
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <p className="text-sm font-medium text-secondary">SMS notifications</p>
              <p className="text-sm text-tertiary">Receive updates via text message</p>
            </div>
            <span className={cn(!hasCellPhone && "opacity-50")}>
              <Switch
                checked={contactByPhone}
                onCheckedChange={(checked) => handleToggle("contact_by_phone", checked)}
                disabled={isUpdating || !hasCellPhone}
                aria-label="SMS notifications"
              />
            </span>
          </div>

          {!hasCellPhone && (
            <p className="text-sm text-secondary">
              Add a cell phone to your profile to receive texts.{" "}
              <button
                onClick={onScrollToCellPhone}
                className="text-brand underline font-medium hover:opacity-80 transition-opacity"
              >
                Add cell phone
              </button>
            </p>
          )}
        </div>

        <p className="text-2xs text-secondary">
          Notifications include a client accepting your invite, purchasing a plan, or something
          ready for your review. Changes may take up to 24 hours to apply.
        </p>
      </div>
    </div>
  );
};
